import { useCallback, useState } from 'react'
import { Message } from '@arco-design/web-react'

/**
 * 封装敏感操作调用流程：
 * 1) 先执行 apiCall
 * 2) 若后端返回需要二次验证（403 / verification_required），
 *    主动触发验证 Modal，验证成功后再次重试 apiCall
 */
export function useSecureVerification() {
  const [verifyVisible, setVerifyVisible] = useState(false)
  const [pendingCall, setPendingCall] = useState<(() => Promise<unknown>) | null>(null)
  const [resolveRef, setResolveRef] = useState<{
    resolve: (v: unknown) => void
    reject: (e: unknown) => void
  } | null>(null)

  const runSecure = useCallback(async <T,>(apiCall: () => Promise<T>): Promise<T> => {
    try {
      return await apiCall()
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
      const msg = e?.response?.data?.message || e?.message || ''
      const needVerify =
        e?.response?.status === 403 &&
        (msg.includes('验证') || msg.toLowerCase().includes('verification'))
      if (!needVerify) throw err

      // 弹出验证
      return new Promise<T>((resolve, reject) => {
        setPendingCall(() => apiCall as () => Promise<unknown>)
        setResolveRef({ resolve: resolve as (v: unknown) => void, reject })
        setVerifyVisible(true)
      })
    }
  }, [])

  const handleSuccess = useCallback(async () => {
    setVerifyVisible(false)
    if (pendingCall && resolveRef) {
      try {
        const data = await pendingCall()
        resolveRef.resolve(data)
      } catch (err) {
        Message.error(err instanceof Error ? err.message : '操作失败')
        resolveRef.reject(err)
      }
    }
    setPendingCall(null)
    setResolveRef(null)
  }, [pendingCall, resolveRef])

  const handleCancel = useCallback(() => {
    setVerifyVisible(false)
    if (resolveRef) {
      resolveRef.reject(new Error('用户取消了二次验证'))
    }
    setPendingCall(null)
    setResolveRef(null)
  }, [resolveRef])

  return {
    runSecure,
    verifyVisible,
    handleSuccess,
    handleCancel,
  }
}
